import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration.js';
dayjs.extend(duration);

// Escape characters with special meaning either inside or outside character sets.
// Use a simple backslash escape when it's always valid, and a `\xnn` escape when the simpler form would be disallowed by Unicode patterns' stricter grammar.
export const escapeRegex = (input: string) =>
  input.replaceAll(/[$()*+.?[\\\]^{|}]/g, String.raw`\$&`).replaceAll('-', String.raw`\x2d`);

/** Replaces CR/LF runs with a single space so interpolated text cannot inject extra lines. */
export const stripNewlines = (value: string): string => value.replace(/[\r\n]+/g, ' ');

/**
 * Trim a nullable string and coerce blank / whitespace-only values to `null`.
 *
 * Use for nullable varchar/text columns that have no empty-string constraint: cleared form
 * fields are often persisted as `''`, and nullish coalescing (`??`) / `!= null` treat those as
 * present. Prefer this over `value ?? null` when empty should mean missing.
 */
export const trimToNull = (value: string | null | undefined): string | null =>
  value?.trim() || null;

/** Tag used to frame untrusted (provider- or user-supplied) text as literal data inside LLM prompts. */
export const UNTRUSTED_PROMPT_DATA_TAG = 'untrusted-data';

/**
 * Frames provider- or user-supplied text as literal DATA inside an LLM prompt so a malicious value
 * cannot be interpreted as instructions (prompt-injection defense). Newlines are collapsed and angle
 * brackets stripped (defense in depth) so the value cannot break out of the delimiter or forge its
 * own tags. Pair with a one-line instruction telling the model that text inside `<untrusted-data>`
 * tags is data to be treated literally and never followed as instructions.
 */
export const wrapUntrustedPromptData = (value: string): string => {
  const neutralized = stripNewlines(value).replace(/[<>]/g, ' ').trim();

  return `<${UNTRUSTED_PROMPT_DATA_TAG}>${neutralized}</${UNTRUSTED_PROMPT_DATA_TAG}>`;
};

/** Strip leading/trailing punctuation from the query; trim outer whitespace. */
export const normalizeListingsSearchQuery = (input: string): string => {
  const trimmed = input.trim();

  if (!trimmed) {
    return '';
  }

  return trimmed.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
};

/**
 * Converts a string to capitalize the first letter of each word.
 * Naming comes from https://en.wikipedia.org/wiki/Letter_case#:~:text=Start%20case
 *
 * @example
 * "tempor duis aute amet quis." -> "Tempor Duis Aute Amet Quis."
 */
export const toStartCase = (input: string) =>
  input
    .toLowerCase()
    .replaceAll('_', ' ')
    .split(' ')
    .map(s => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');

/**
 * Turns a camelCase identifier into readable title case, keeping acronym runs intact.
 *
 * @example
 * "adyenForPlatformsAdvanced" -> "Adyen For Platforms Advanced"
 * "pciDSSQuestionnaire" -> "Pci DSS Questionnaire"
 */
export const camelCaseToTitleCase = (input: string): string =>
  input
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/**
 * Takes first two initials, or first two letters if displayName is one word
 */
export const getInitials = (displayName: string) =>
  [...displayName.split(' ').map(name => name[0]), displayName[1]]
    .join('')
    .slice(0, 2)
    .toUpperCase();

type PluralizePluralOptions = {
  plural: string;
  suffix?: never;
};
type PluralizeSuffixOptions = {
  suffix: string;
  plural?: never;
};

type PluralizeOptions = { undefinedCase?: string } & (
  | PluralizePluralOptions
  | PluralizeSuffixOptions
);

const defaultOptions: PluralizeOptions = { suffix: 's' };

/**
 * Pluralize a singular term based on a count, with support for custom plural forms, suffixes,
 * and handling for undefined counts.
 *
 * @param count - The number determining whether to use the singular or plural form.
 *                If `undefined`, the behavior is controlled by the `undefinedCase` option.
 * @param singular - The singular form of the term (e.g., "apple").
 * @param options - Configuration for pluralization behavior.
 *
 * ### Options
 * - `plural` (string): Explicit plural form of the term (e.g., "apples").
 * - `suffix` (string): A suffix to append to the singular term to form the plural (e.g., "s").
 * - `undefinedCase` (string): A value to return when `count` is `undefined`.
 *
 * **Note**: You must provide either the `plural` or `suffix` option, but not both.
 *
 * ### Examples
 *
 * #### Using Explicit Plural Form
 * ```typescript
 * pluralize(1, "apple", { plural: "apples" }); // "apple"
 * pluralize(5, "apple", { plural: "apples" }); // "apples"
 * pluralize(undefined, "apple", { plural: "apples", undefinedCase: "none" }); // "none"
 * ```
 *
 * #### Using Suffix
 * ```typescript
 * pluralize(1, "hour", { suffix: "s" }); // "hour"
 * pluralize(5, "hour", { suffix: "s" }); // "hours"
 * pluralize(undefined, "hour", { suffix: "s", undefinedCase: "none" }); // "none"
 * ```
 *
 * #### Default Suffix Behavior
 * ```typescript
 * pluralize(1, "cat"); // "cat"
 * pluralize(3, "cat"); // "cats"
 * pluralize(undefined, "cat", { undefinedCase: "unknown" }); // "unknown"
 * ```
 *
 * @returns The singular or plural form of the term, or the `undefinedCase` value if `count` is undefined.
 */
export const pluralize = (
  count: number | undefined | null,
  singular: string,
  options: PluralizeOptions = defaultOptions,
) => {
  const plural = options.plural ?? `${singular}s`;
  if (options.undefinedCase && count == null) {
    return options.undefinedCase;
  }

  return count === 1 || count == null ? singular : plural;
};

/**
 * Joins an array of strings into a single string with customizable separators.
 *
 * This function filters out `null` and `undefined` values from the input array,
 * then concatenates the remaining strings using the specified separators.
 *
 * @param {Array<string | undefined | null>} inputArray - The array of strings to join.
 *   `null` and `undefined` values are ignored.
 * @param {string} [separator=', '] - The separator to use between all elements except the last.
 * @param {string | null} [lastSeparator='and'] - The separator to use before the last element.
 * @returns {string} - A concatenated string of the array elements.
 *
 * @example
 * joinWithSeparators(['apple', 'banana', 'cherry']);
 * // Returns: 'apple, banana, and cherry'
 *
 * @example
 * joinWithSeparators(['apple', 'banana'], ', ', 'or');
 * // Returns: 'apple or banana'
 *
 * @example
 * joinWithSeparators(['apple']);
 * // Returns: 'apple'
 *
 * @example
 * joinWithSeparators([]);
 * // Returns: ''
 */
export const joinWithSeparators = (
  inputArray: Array<string | undefined | null>,
  separator = ', ',
  lastSeparator: string | null = ' and ',
): string => {
  const array = inputArray.filter(Boolean);

  if (array.length === 0) {
    return '';
  }

  const [item] = array;

  if (array.length === 1 && item) {
    return item;
  }

  if (lastSeparator === null) {
    return array.join(separator);
  }

  if (array.length === 2) {
    return `${array[0]}${lastSeparator}${array[1]}`;
  }

  return `${array.slice(0, -1).join(separator)}${separator.trimEnd()}${lastSeparator}${array.at(-1)}`;
};

export const formatJsonNameList = (raw: string | null | undefined): string | null => {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return null;
    }
    const names = parsed.filter((n): n is string => typeof n === 'string');
    if (names.length === 0) {
      return null;
    }
    if (names.length === 1) {
      return names[0] ?? null;
    }
    if (names.length === 2) {
      return `${names[0]} and ${names[1]}`;
    }

    return `${names.slice(0, -1).join(', ')}, and ${names.at(-1)}`;
  } catch {
    return null;
  }
};

export const removeDiacritics = (input: string) =>
  input.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/** Lowercase label with punctuation collapsed to spaces, for fuzzy equality checks. */
export const normalizeComparableLabel = (value: string): string =>
  value
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, ' ')
    .trim();

/** Unicode-aware display name normalization for exact equality checks (venues, listings, rooms). */
export const normalizeDisplayNameForMatch = (value: string): string =>
  value
    .normalize('NFC')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// Allow to partial match individual prefixes
export const sanitizeAndFuzzifyQuery = (input: string): string => {
  const clean = removeDiacritics(input)
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim();

  if (!clean) {
    return '';
  }

  return clean
    .split(/\s+/)
    .filter(term => term)
    .map(term => `${term}:*`)
    .join(' & ');
};

export const getFirstNameAndLastName = (name: string) => {
  const nameParts = name.trim().split(/\s+/);
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  return { firstName, lastName };
};

type FormatUserDisplayNameOptions = {
  /**
   * If true, appends the email in parentheses after the display name.
   * @default false
   */
  includeEmail?: boolean;
  /**
   * Controls the fallback behavior when name parts are missing.
   * - 'email': Falls back to email if both first and last name are missing (default)
   * - 'partial': Allows partial names (first only or last only)
   * - 'strict': Falls back to email unless both first AND last name are present
   * @default 'email'
   */
  fallback?: 'email' | 'partial' | 'strict';
  /**
   * When using partial names, controls which name part to prefer if only one is available.
   * - 'first': Prefer first name if only one is available (default)
   * - 'last': Prefer last name if only one is available
   * - 'any': Use whichever is available
   * @default 'first'
   */
  partialPreference?: 'first' | 'last' | 'any';
};

/**
 * Converts a duration in minutes into a human-readable string.
 *
 * @example
 * humanizeMinutes(60)    // "1 hour"
 * humanizeMinutes(120)   // "2 hours"
 * humanizeMinutes(90)    // "1 hour and 30 minutes"
 * humanizeMinutes(44760) // "1 month, 1 day and 1 hour"
 */
export const humanizeMinutes = (minutes: number): string => {
  const duration = dayjs.duration(minutes, 'minutes');
  const months = duration.months();
  const days = duration.days();
  const hours = duration.hours();
  const remainingMinutes = duration.minutes();

  const parts: string[] = [];

  if (months > 0) {
    parts.push(`${months} ${pluralize(months, 'month')}`);
  }
  if (days > 0) {
    parts.push(`${days} ${pluralize(days, 'day')}`);
  }
  if (hours > 0) {
    parts.push(`${hours} ${pluralize(hours, 'hour')}`);
  }
  if (remainingMinutes > 0) {
    parts.push(`${remainingMinutes} ${pluralize(remainingMinutes, 'minute')}`);
  }

  if (parts.length === 0) {
    return '0 minutes';
  }

  return joinWithSeparators(parts);
};

export const formatUserDisplayName = (
  user: {
    firstName?: string | null | undefined;
    lastName?: string | null | undefined;
    email: string;
  },
  options: FormatUserDisplayNameOptions = {},
) => {
  const { includeEmail = false, fallback = 'email', partialPreference = 'first' } = options;

  const hasFirstName = !!user.firstName;
  const hasLastName = !!user.lastName;

  if (!hasFirstName && !hasLastName) {
    return user.email;
  }

  // Strict mode: require both first and last name
  if (fallback === 'strict' && (!hasFirstName || !hasLastName)) {
    return user.email;
  }

  // Partial mode: handle single name part based on preference
  if (fallback === 'partial' && hasFirstName !== hasLastName) {
    if (partialPreference === 'first' && !!user.firstName) {
      const displayName = user.firstName;

      return includeEmail ? `${displayName} (${user.email})` : displayName;
    }
    if (partialPreference === 'last' && !!user.lastName) {
      const displayName = user.lastName;

      return includeEmail ? `${displayName} (${user.email})` : displayName;
    }
    if (partialPreference === 'any') {
      const displayName = user.firstName || user.lastName;

      return includeEmail ? `${displayName} (${user.email})` : displayName;
    }
  }

  // Default behavior: combine available name parts
  const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ');

  if (includeEmail) {
    return `${displayName} (${user.email})`;
  }

  return displayName;
};
